import { Inject, Injectable } from '@angular/core';
import { AuxiliaryDataFieldType } from '@vendasta/account-group';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ObjectType, SchemaService } from '@vendasta/auxiliary-data-components';
import { LoadRequest, TableDataService, VaFilteredMatTableService } from '@vendasta/va-filter2-table';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { SELECTED_OBJECT_TYPE$$, VIEWING_ARCHIVED$$ } from './tokens';

@Injectable({
  providedIn: 'root',
})
export class AuxiliaryDataTableService implements TableDataService<AuxiliaryDataFieldInterface> {
  loading$$ = new BehaviorSubject<boolean>(false);
  // DO NOT REMOVE
  // This table is backed by vStore, this is the only
  // reliable way of figuring out the totals
  // with a limit of 100 custom fields per PID today
  readonly totalResults$: Observable<number> = combineLatest([
    this.partnerId$,
    this.selectedObjectType$$,
    this.loading$$,
  ]).pipe(
    filter(([, , loading]) => loading === true),
    switchMap(([pid, objectType]) =>
      this.schemaService.listFieldSchemas(objectType, pid, this.viewingArchived$$.value, '', 100),
    ),
    map((resp) => resp?.fieldSchemas?.length || 0),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  private cursors: { [id: number]: string } = { 0: '' };

  constructor(
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    @Inject(VIEWING_ARCHIVED$$) private readonly viewingArchived$$: BehaviorSubject<boolean>,
    @Inject(SELECTED_OBJECT_TYPE$$) private readonly selectedObjectType$$: BehaviorSubject<ObjectType>,
    private readonly schemaService: SchemaService,
    private readonly vaFilteredMatTableService: VaFilteredMatTableService<AuxiliaryDataFieldInterface>,
  ) {}

  reload(): void {
    // There's no reload() in the table library, just reset its paging:
    // https://vendasta.slack.com/archives/C0QB93DN0/p1599606306086700
    this.vaFilteredMatTableService.resetPaging();
  }

  load(request: LoadRequest): Observable<AuxiliaryDataFieldInterface[]> {
    const { pageIndex, pageSize } = request;
    if (pageIndex === 0) {
      this.cursors = { 0: '' };
    }
    const nextCursor = this.cursors[request.pageIndex] || '';
    return this.partnerId$.pipe(
      tap(() => this.loading$$.next(true)),
      withLatestFrom(this.selectedObjectType$$),
      switchMap(([pid, objectType]) =>
        this.schemaService.listFieldSchemas(objectType, pid, this.viewingArchived$$.value, nextCursor, pageSize),
      ),
      take(1),
      tap((response) => {
        if (response?.pagingMetadata) {
          this.cursors[request.pageIndex + 1] = response.pagingMetadata?.nextCursor;
        }
      }),
      map((retVal) => {
        const fields: AuxiliaryDataFieldInterface[] = [];
        retVal?.fieldSchemas?.forEach((field) => {
          let typeString = '';
          switch (field.fieldType) {
            case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_STRING:
              typeString = 'COMMON.AUXILIARY_DATA.TYPES.STRING';
              break;
            case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_INTEGER:
              typeString = 'COMMON.AUXILIARY_DATA.TYPES.INTEGER';
              break;
            case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DATE:
              typeString = 'COMMON.AUXILIARY_DATA.TYPES.DATE';
              break;
            case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DROPDOWN:
              typeString = 'COMMON.AUXILIARY_DATA.TYPES.DROPDOWN';
              break;
            case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_CURRENCY:
              typeString = 'COMMON.AUXILIARY_DATA.TYPES.CURRENCY';
              break;
            default:
              console.warn(`unknown field type for field ${field.fieldId}: `, field.fieldType);
              break;
          }
          fields.push({
            id: field.fieldId,
            name: field.fieldName,
            description: field.fieldDescription,
            type: typeString,
            archived: !!field.archived,
            externalId: field.externalId,
          });
        });
        return fields;
      }),
      tap(() => this.loading$$.next(false)),
    );
  }
}

export interface AuxiliaryDataFieldInterface {
  id: string;
  name: string;
  description: string;
  type: string;
  archived: boolean;
  externalId: string;
}
